import { FilesApiConstants } from './files.constants';
import { AddTag } from './models/add-tags.model';
import { CreatePath } from './models/create-path.model';
import { DeleteTag } from './models/delete-tag.model';
import { DocTag } from './models/doc-tag.model';
import { DocumentExportJob } from './models/document-export-job.model';
import { DocumentExportJobStatus } from './models/document-export-job-status.enum';
import { DocumentExportSubmitResponse } from './models/document-export-submit-response.model';
import { DownloadDocuments } from './models/download-documents.model';
import { EditName } from './models/edit-name.model';
import { RequestItem } from './models/request-item.model';
import { SharedExportDownloadResponse } from './models/shared-export-download-response.model';
import { SharedExportEstimateResponse } from './models/shared-export-estimate-response.model';
import { SharedExportRequest } from './models/shared-export-request.model';
import { TagType } from './models/tag-type.model';
import { Injectable } from '@angular/core';
import { FileUploadStorage } from '@program/shared/formio-custom-components/file-upload-storage.model';
import { FORM_TYPE } from '@program/shared/formio-custom-components/form-type.enum';
import { CopyDocuments } from '@shared/components/files/shared/models/copy-documents.model';
import { DeleteDocuments } from '@shared/components/files/shared/models/delete-documents.model';
import { ShareDocuments } from '@shared/components/files/shared/models/share-documents.model';
import { Organization } from '@shared/models/organization/organization.model';
import { BaseRestApiService } from '@shared/services/base-rest-api.service';
import { RestApiWrapperService } from '@shared/services/rest-api-wrapper.service';
import { triggerIframeDownload as triggerSharedIframeDownload } from '@shared/utils/iframe-download.util';
import { EventService } from '@shared/event.service';
import { User } from '@user/user.model';
import { UserService } from '@user/user.service';
import { Observable, timer } from 'rxjs';
import { flatMap, switchMap, takeWhile } from 'rxjs/operators';

/**
 * DocumentDataService: communicate with the backend
 */
@Injectable({
  providedIn: 'root',
})
export class DocumentDataService extends BaseRestApiService {
  private organization: Organization;

  constructor(
    _restApiWrapperService: RestApiWrapperService,
    private _userService: UserService,
    private _eventService: EventService
  ) {
    super(_restApiWrapperService);
  }

  getList(organizationId: string): Observable<any[]> {
    // use a specific organization.id for Investor's Portfolio Data Room
    if (organizationId && organizationId !== '') {
      return this.get<any[]>(FilesApiConstants.document.list(organizationId));
    }
  }

  getSharedList(organizationId: string): Observable<any[]> {
    // use a specific organization.id for Investor's Portfolio Data Room
    if (organizationId && organizationId !== '') {
      return this.get<any[]>(
        FilesApiConstants.document.sharedList(organizationId)
      );
    }
  }

  getSharingOrganizations(organizationId: string): Observable<Array<{ id: string; name: string }>> {
    if (organizationId && organizationId !== '') {
      return this.get<Array<{ id: string; name: string }>>(
        FilesApiConstants.document.sharingOrganizations(organizationId)
      );
    }
  }

  getSharedListByOwnerOrganization(
    organizationId: string,
    ownerOrganizationId: string
  ): Observable<any[]> {
    if (organizationId && organizationId !== '') {
      return this.get<any[]>(
        FilesApiConstants.document.sharedListByOwner(organizationId),
        { params: { ownerOrganizationId } }
      );
    }
  }

  // this can get a documents list by tag. using for Read only mode.
  getListByTag(
    organizationId: string,
    docTag: DocTag,
    includeSponsorDocs: boolean = false
  ): Observable<any[]> {
    if (organizationId && organizationId !== '') {
      return this.post<any[]>(
        FilesApiConstants.document.tagFiles(organizationId, includeSponsorDocs),
        docTag
      );
    }
  }

  addUserTag(documentTag: AddTag): Observable<any> {
    return this._userService.getCurrentUser().pipe(
      flatMap((user: User) => {
        return this.post<any>(
          FilesApiConstants.document.tag.user(user.organization.id),
          documentTag
        );
      })
    );
  }

  addSystemTag(documentTag: AddTag): Observable<any> {
    return this._userService.getCurrentUser().pipe(
      flatMap((user: User) => {
        return this.post<any>(
          FilesApiConstants.document.tag.system(user.organization.id),
          documentTag
        );
      })
    );
  }

  disassociateTag(documentTag: DeleteTag): Observable<any[]> {
    return this._userService.getCurrentUser().pipe(
      flatMap((user: User) => {
        return this.post<any[]>(
          FilesApiConstants.document.tag.disassociate(user.organization.id),
          documentTag
        );
      })
    );
  }

  createFolder(createPath: CreatePath): Observable<any> {
    return this._userService.getCurrentUser().pipe(
      flatMap((user: User) => {
        return this.post(
          FilesApiConstants.document.path(user.organization.id),
          createPath
        );
      })
    );
  }

  editName(editName: EditName) {
    return this._userService.getCurrentUser().pipe(
      flatMap((user: User) => {
        return this.post(
          FilesApiConstants.document.editName(user.organization.id),
          editName
        );
      })
    );
  }

  moveCopyDocuments(
    requestItems: RequestItem[],
    destinationTagType: TagType,
    type: string
  ) {
    return this._userService.getCurrentUser().pipe(
      flatMap((user: User) => {
        const documents: CopyDocuments = {
          organization: user.organization,
          requestItems: requestItems,
          systemDocumentType: destinationTagType.docTagType,
          index: destinationTagType.docTagItemId,
        };

        switch (type) {
          case 'copy':
            return this.post(
              FilesApiConstants.document.copy(user.organization.id),
              documents
            );

          case 'move':
            return this.post(
              FilesApiConstants.document.move(user.organization.id),
              documents
            );
        }
      })
    );
  }

  deleteDocuments(requestItems: RequestItem[]) {
    return this._userService.getCurrentUser().pipe(
      flatMap((user: User) => {
        const documents: DeleteDocuments = {
          organization: user.organization,
          documentKeys: requestItems,
        };
        return this.post(
          FilesApiConstants.document.delete(user.organization.id),
          documents
        );
      })
    );
  }

  /**
   * Request a download token from the backend.
   * The token is short-lived (60s) and single-use.
   * Callers should immediately navigate the browser to the GET download URL.
   */
  getDownloadToken(
    selectedKeys: string[],
    targetOrganizationId: string
  ): Observable<{ token: string; expiresIn: number }> {
    const body: DownloadDocuments = {
      organizationId: targetOrganizationId,
      selectedKeys,
    };
    return this.post<{ token: string; expiresIn: number }>(
      FilesApiConstants.document.downloadToken(targetOrganizationId),
      body,
      { responseType: 'json' }
    );
  }

  /**
   * Build the browser-native download URL using a token.
   */
  getDownloadUrl(organizationId: string, token: string): string {
    return `${FilesApiConstants.document.download(organizationId)}?token=${encodeURIComponent(token)}`;
  }

  /**
   * Trigger the browser-native token GET download flow.
   */
  downloadWithToken(organizationId: string, token: string, fallbackFilename?: string): Observable<void> {
    void fallbackFilename;
    const url = this.getDownloadUrl(organizationId, token);
    return this.triggerIframeDownload(url);
  }

  /**
   * Point the shared hidden iframe at a browser-navigable download URL.
   *
   * <p>Delegates to the shared {@link triggerIframeDownload} utility so this
   * security-sensitive handoff has a single implementation across the app (see
   * `@shared/utils/iframe-download.util`). Used by both {@link downloadWithToken} (legacy
   * token flow) and {@link downloadExportJob} (async export download-redirect).
   */
  private triggerIframeDownload(url: string): Observable<void> {
    return triggerSharedIframeDownload(url, (message: string) => this._eventService.error(message));
  }

  estimateSharedExport(
    organizationId: string,
    request: SharedExportRequest
  ): Observable<SharedExportEstimateResponse> {
    return this.post<SharedExportEstimateResponse>(
      FilesApiConstants.document.sharedExportEstimate(organizationId),
      this.cleanSharedExportRequest(request),
      { responseType: 'json' }
    );
  }

  /**
   * @deprecated Replaced by {@link submitSharedExport}. Retained only so the legacy modal code
   * compiles during the rollout window; remove once the backend {@code POST /shared-export/download/token}
   * endpoint is deleted (gated on frontend cutover validation against staging — see the async
   * shared document export exec plan M10 in the oes-dt-api repo).
   */
  getSharedExportDownloadToken(
    organizationId: string,
    request: SharedExportRequest
  ): Observable<SharedExportDownloadResponse> {
    return this.post<SharedExportDownloadResponse>(
      FilesApiConstants.document.sharedExportDownloadToken(organizationId),
      this.cleanSharedExportRequest(request),
      { responseType: 'json' }
    );
  }

  // ---------- Async shared-document export (new flow) ----------

  /**
   * Submit a new shared-document export. Always returns {@code QUEUED} status. The response
   * {@code mode} tells the caller whether to poll (INLINE) or show a "we'll email you" toast
   * (ASYNC).
   */
  submitSharedExport(
    organizationId: string,
    request: SharedExportRequest
  ): Observable<DocumentExportSubmitResponse> {
    return this.post<DocumentExportSubmitResponse>(
      FilesApiConstants.document.sharedExportSubmit(organizationId),
      this.cleanSharedExportRequest(request),
      { responseType: 'json' }
    );
  }

  /**
   * Fetch current state of a single export job owned by the current user. Backend intentionally
   * returns soft-deleted rows with {@code deletedAt} populated so the frontend can distinguish
   * DELETED from not-found without parsing typed 410 payloads from the download endpoint.
   */
  getExportJob(organizationId: string, jobId: string): Observable<DocumentExportJob> {
    return this.get<DocumentExportJob>(
      FilesApiConstants.document.documentExport(organizationId, jobId)
    );
  }

  /**
   * List the current user's non-deleted export jobs, newest first, capped at 50 rows.
   */
  listExportJobs(organizationId: string): Observable<DocumentExportJob[]> {
    return this.get<DocumentExportJob[]>(
      FilesApiConstants.document.documentExports(organizationId)
    );
  }

  /**
   * Soft-delete an export job. Idempotent — a second call against an already-deleted job
   * returns 204 and is a no-op. If the job is {@code COMPLETED}, the backing S3 object is
   * also deleted.
   */
  deleteExportJob(organizationId: string, jobId: string): Observable<void> {
    return this.delete<void>(
      FilesApiConstants.document.documentExport(organizationId, jobId)
    );
  }

  /**
   * Poll the status of an export job until it reaches a terminal state (COMPLETED, FAILED,
   * or EXPIRED). Emits on every poll and completes after the terminal emission so callers
   * can use {@code .subscribe(state => ...)} with a single handler.
   */
  pollExportJob(
    organizationId: string,
    jobId: string,
    intervalMs: number = 1500
  ): Observable<DocumentExportJob> {
    return timer(0, intervalMs).pipe(
      switchMap(() => this.getExportJob(organizationId, jobId)),
      takeWhile((job) => !DocumentDataService.isTerminalStatus(job.status), true)
    );
  }

  /**
   * Build the authenticated API URL used to request a presigned S3 URL for a completed export.
   */
  getExportDownloadUrl(organizationId: string, jobId: string): string {
    return FilesApiConstants.document.documentExportDownload(organizationId, jobId);
  }

  /**
   * Initiate a browser-native download of a completed export. First calls the authenticated
   * API endpoint that returns a short-lived presigned S3 URL, then points the hidden iframe at
   * that presigned URL so the browser handles the file download natively.
   *
   * <p>Callers must verify {@code status === COMPLETED} via {@link getExportJob} before
   * calling this. The presigned-URL lookup runs through {@code HttpClient}, so the normal JWT
   * interceptor attaches the bearer token. Direct iframe navigation to the protected API route
   * would 401 because browser navigation does not include that header.
   */
  downloadExportJob(organizationId: string, jobId: string): Observable<void> {
    return this.get<{ presignedUrl: string }>(
      this.getExportDownloadUrl(organizationId, jobId)
    ).pipe(
      switchMap((response) => this.triggerIframeDownload(response.presignedUrl))
    );
  }

  private static isTerminalStatus(status: DocumentExportJobStatus): boolean {
    return (
      status === DocumentExportJobStatus.COMPLETED ||
      status === DocumentExportJobStatus.FAILED ||
      status === DocumentExportJobStatus.EXPIRED
    );
  }

  /**
   * Download file as a blob (for preview rendering in browser).
   */
  downloadDocumentsAsBlob(
    selectedKeys: string[],
    targetOrganizationId: string
  ): Observable<Blob> {
    const body: DownloadDocuments = {
      organizationId: targetOrganizationId,
      selectedKeys,
    };
    return this.post<Blob>(
      FilesApiConstants.document.download(targetOrganizationId),
      body,
      { responseType: 'blob' as const }
    );
  }

  downloadDocumentById(documentId: string): Observable<any[]> {
    return this.post<string>(
      FilesApiConstants.formDocument.prepareDownload(documentId),
      null,
      { responseType: 'json' }
    ).pipe(
      flatMap((download: any) => {
        const downloadOption = {
          responseType: 'blob' as const,
          params: {
            documentKey: download?.downloadId,
          },
        };
        return this.get<any[]>(
          FilesApiConstants.formDocument.download(),
          downloadOption
        );
      })
    );
  }

  uploadDocuments(file: Object, option?: Object): Observable<any> {
    return this._userService.getCurrentUser().pipe(
      flatMap((user: User) => {
        return this.post<any>(
          FilesApiConstants.document.upload(user.organization.id),
          file,
          option
        );
      })
    );
  }

  public uploadMilestoneDocuments(file: Object, orgId: string, option?: Object): Observable<any> {
    return this.post<any>(
      FilesApiConstants.document.upload(orgId),
      file,
      option
    );
  }

  shareDocuments(shareDocuments: ShareDocuments) {
    return this._userService.getCurrentUser().pipe(
      flatMap((user: User) => {
        return this.post(
          FilesApiConstants.document.share(user.organization.id),
          shareDocuments
        );
      })
    );
  }

  shareItems(docs: Array<any>, recipients: Array<Organization>) {
    const requestItems: Array<any> = new Array<any>();
    recipients.forEach((recipient) => {
      requestItems.push(
        docs.map((doc) => {
          return {
            documentKey: doc.documentKey,
            recipient: recipient,
          };
        })
      );
    });
    const shareRequest: ShareDocuments = {
      organization: this.organization,
      requestItems: requestItems,
    };
    return this.shareDocuments(shareRequest);
  }

  setFileUploadStorageModel(
    organizationId: string,
    formType: FORM_TYPE,
    index: string,
    externalUser: boolean = false
  ) {
    const storageModel = new FileUploadStorage({
      organizationId,
      formType,
      index,
    });
    sessionStorage.setItem(
      'file-upload-storage-model',
      JSON.stringify(storageModel)
    );
    const userType = externalUser ? 'external' : 'standard';
    sessionStorage.setItem('user-type', userType);
  }

  private cleanSharedExportRequest(
    request: SharedExportRequest
  ): SharedExportRequest {
    const cleanedRequest: SharedExportRequest = {
      programId: request.programId,
      associationTypes: request.associationTypes,
    };

    if (request.filenameContains) {
      cleanedRequest.filenameContains = request.filenameContains;
    }
    if (request.extensions?.length) {
      cleanedRequest.extensions = request.extensions;
    }
    if (request.uploadDateFrom) {
      cleanedRequest.uploadDateFrom = request.uploadDateFrom;
    }
    if (request.uploadDateTo) {
      cleanedRequest.uploadDateTo = request.uploadDateTo;
    }
    if (request.formUpload !== undefined) {
      cleanedRequest.formUpload = request.formUpload;
    }

    return cleanedRequest;
  }
}
